// FILE: src/pages/SaaSDashboard.jsx
import React from 'react';
import Card from '../components/ui/Card';
import ChartCard from '../components/ui/ChartCard';
import DataTable from '../components/ui/DataTable';

const SaaSDashboard = () => {
  // SaaS KPIs
  const saasKPIs = [
    {
      title: 'Monthly Recurring Revenue',
      value: '$127,340',
      change: '+8.2%',
      changeType: 'increase',
      icon: '💰',
      description: 'from last month'
    },
    {
      title: 'Active Subscribers',
      value: '2,847',
      change: '+12.5%',
      changeType: 'increase',
      icon: '👥',
      description: 'from last month'
    },
    {
      title: 'Churn Rate',
      value: '2.3%',
      change: '-0.8%',
      changeType: 'decrease',
      icon: '📉',
      description: 'from last month'
    },
    {
      title: 'Customer LTV',
      value: '$4,250',
      change: '+15.3%',
      changeType: 'increase',
      icon: '📊',
      description: 'from last month'
    },
    {
      title: 'Net Revenue Retention',
      value: '108%',
      change: '+3.2%',
      changeType: 'increase',
      icon: '🔄',
      description: 'from last month'
    }
  ];

  // Subscription Tier Data
  const subscriptionTiers = [
    { tier: 'Free', subscribers: 1247, percentage: '43.8%', revenue: '$0', color: 'bg-gray-500' },
    { tier: 'Basic', subscribers: 856, percentage: '30.1%', revenue: '$25,680', color: 'bg-blue-500' },
    { tier: 'Pro', subscribers: 523, percentage: '18.4%', revenue: '$52,300', color: 'bg-purple-500' },
    { tier: 'Enterprise', subscribers: 221, percentage: '7.7%', revenue: '$49,360', color: 'bg-green-500' }
  ];

  // Recent Subscriptions Data
  const recentSubscriptions = [
    {
      id: 1,
      customer: 'John Smith',
      email: 'john@company.com',
      plan: 'Pro',
      amount: '$99/month',
      status: 'Active',
      startDate: '2024-01-15',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
    },
    {
      id: 2,
      customer: 'Sarah Johnson',
      email: 'sarah@startup.io',
      plan: 'Enterprise',
      amount: '$299/month',
      status: 'Active',
      startDate: '2024-01-14',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b47c?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
    },
    {
      id: 3,
      customer: 'Michael Chen',
      email: 'm.chen@tech.com',
      plan: 'Basic',
      amount: '$29/month',
      status: 'Trial',
      startDate: '2024-01-13',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
    },
    {
      id: 4,
      customer: 'Emily Davis',
      email: 'emily@agency.com',
      plan: 'Pro',
      amount: '$99/month',
      status: 'Cancelled',
      startDate: '2024-01-12',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
    }
  ];

  // Usage Analytics Data
  const usageStats = [
    { metric: 'API Calls', value: '2.4M', limit: '5M', usage: 48 },
    { metric: 'Storage Used', value: '127 GB', limit: '500 GB', usage: 25 },
    { metric: 'Bandwidth', value: '890 GB', limit: '1 TB', usage: 89 },
    { metric: 'Users', value: '847', limit: '1000', usage: 85 }
  ];

  const subscriptionColumns = [
    {
      key: 'avatar',
      label: '',
      render: (value, row) => (
        <img 
          src={value} 
          alt={row.customer} 
          className="w-10 h-10 rounded-full object-cover"
        />
      ),
      sortable: false
    },
    {
      key: 'customer',
      label: 'Customer',
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-white">{value}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{row.email}</div>
        </div>
      )
    },
    {
      key: 'plan',
      label: 'Plan',
      render: (value) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
          value === 'Enterprise' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
          value === 'Pro' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' :
          value === 'Basic' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
          'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
        }`}>
          {value}
        </span>
      )
    },
    { key: 'amount', label: 'Amount' },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
          value === 'Active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
          value === 'Trial' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' :
          'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
        }`}>
          {value}
        </span>
      )
    },
    { key: 'startDate', label: 'Start Date' },
    {
      key: 'actions',
      label: 'Actions',
      render: () => (
        <div className="flex space-x-2">
          <button className="text-blue-600 hover:text-blue-700 text-sm">View</button>
          <button className="text-green-600 hover:text-green-700 text-sm">Extend</button>
          <button className="text-red-600 hover:text-red-700 text-sm">Cancel</button>
        </div>
      ),
      sortable: false
    }
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">SaaS Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Monitor your subscription business metrics</p>
        </div>
        <div className="flex space-x-3">
          <button className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
            Export Report
          </button>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            View Analytics
          </button>
        </div>
      </div>

      {/* SaaS KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {saasKPIs.map((kpi, index) => (
          <Card key={index} className="hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">{kpi.icon}</span>
                </div>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{kpi.title}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{kpi.value}</p>
                <div className="flex items-center mt-1">
                  <span className={`text-sm font-medium ${
                    kpi.changeType === 'increase' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {kpi.change}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">{kpi.description}</span>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* MRR Chart */}
        <ChartCard 
          title="Monthly Recurring Revenue" 
          subtitle="Growth trend over time"
          chartType="area"
          data={[
            { name: 'Jan', mrr: 89000, newMrr: 12000, churnMrr: 3400 },
            { name: 'Feb', mrr: 95000, newMrr: 14000, churnMrr: 4200 },
            { name: 'Mar', mrr: 102000, newMrr: 15000, churnMrr: 3800 },
            { name: 'Apr', mrr: 108000, newMrr: 16000, churnMrr: 4500 },
            { name: 'May', mrr: 115000, newMrr: 18000, churnMrr: 5000 },
            { name: 'Jun', mrr: 121000, newMrr: 19000, churnMrr: 4800 },
            { name: 'Jul', mrr: 127000, newMrr: 17000, churnMrr: 3900 }
          ]}
          dataKey={['mrr']}
          xAxisKey="name"
          colors={['#10B981']}
        />

        {/* Churn vs New Subscribers Chart */}
        <ChartCard 
          title="Subscriber Activity" 
          subtitle="New vs Churned subscribers"
          chartType="bar"
          data={[
            { name: 'Jan', newSubs: 245, churnedSubs: 67 },
            { name: 'Feb', newSubs: 278, churnedSubs: 89 },
            { name: 'Mar', newSubs: 312, churnedSubs: 76 },
            { name: 'Apr', newSubs: 289, churnedSubs: 94 },
            { name: 'May', newSubs: 356, churnedSubs: 112 },
            { name: 'Jun', newSubs: 334, churnedSubs: 98 },
            { name: 'Jul', newSubs: 398, churnedSubs: 89 }
          ]}
          dataKey={['newSubs', 'churnedSubs']}
          xAxisKey="name"
          colors={['#10B981', '#EF4444']}
        />
      </div>

      {/* Subscription Tiers and Usage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Subscription Tier Breakdown */}
        <ChartCard 
          title="Subscription Tier Breakdown" 
          subtitle="Revenue distribution by plan"
          chartType="donut"
          data={subscriptionTiers.map(tier => ({ name: tier.tier, value: tier.subscribers }))}
          dataKey="value"
          colors={['#6B7280', '#3B82F6', '#8B5CF6', '#10B981']}
        >
          <div className="space-y-4 mt-4">
            {subscriptionTiers.map((tier, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full ${tier.color} mr-3`}></div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{tier.tier}</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-gray-900 dark:text-white">{tier.subscribers} users</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{tier.percentage} • {tier.revenue}</div>
                </div>
              </div>
            ))}
          </div>
        </ChartCard>

        {/* Usage Analytics */}
        <Card title="Usage Analytics" subtitle="Current period consumption">
          <div className="space-y-6">
            {usageStats.map((stat, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{stat.metric}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">{stat.value} / {stat.limit}</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      stat.usage > 80 ? 'bg-red-500' : 
                      stat.usage > 60 ? 'bg-yellow-500' : 
                      'bg-green-500'
                    }`}
                    style={{ width: `${stat.usage}%` }}
                  ></div>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{stat.usage}% used</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Recent Subscriptions */}
      <Card title="Recent Subscriptions" subtitle="Latest subscription activity">
        <DataTable 
          columns={subscriptionColumns}
          data={recentSubscriptions}
          searchable={true}
          pagination={false}
        />
      </Card>
    </div>
  );
};

export default SaaSDashboard;